import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { type MouseEventHandler, useCallback } from 'react';
import type { Product } from '../../../data/product';
import { defineColumn, type StrictColumnDef } from '../../tanstack-table/column';
import { useProductEditStore } from '../product-edit-modal-store';

export const Actions: StrictColumnDef<Product> = {
  ...defineColumn<Product>({
    id: 'Actions',
    minWidth: 60,
    accessorFn: () => '',
    cell: ({ item: product }) => <ActionsCell product={product} />,
  }),
};

function ActionsCell({ product }: { product: Product }) {
  const startEditing = useProductEditStore((s) => s.startEditing);

  const onClick: MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      startEditing(product);
      event.stopPropagation();
    },
    [startEditing, product]
  );

  return (
    /*
      The button fills the entire cell so the click target is the whole
      column-cell — not just the icon. The cell wrapper in `table.tsx` adds
      a 4px gutter (`px-1 py-1`) we can't cancel from here, but the visible
      button area + the inner padding now dwarfs the previous icon-only
      target. Hover tint covers the whole button, which sells the "click
      anywhere here" affordance.
    */
    <button
      type="button"
      onClick={onClick}
      aria-label="Edit"
      className="flex w-full items-center justify-center self-stretch py-2 rounded text-icon transition-colors duration-150 cursor-pointer hover:bg-gray-100 focus:outline-none focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-primary-600"
    >
      <PencilSquareIcon className="size-5" aria-hidden="true" />
    </button>
  );
}
